import React, { useEffect, useState } from "react";
import { Plus, Pencil, Archive, X, ArchiveRestore, Mail, Phone, MapPin } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ConfirmModal } from "@/components/AppDialogs";
import { toast } from "sonner";

/**
 * Suppliers CRUD. Read-only for `finance`; create/update/archive restricted to
 * super_admin (enforced server-side, we hide the buttons accordingly).
 */
export default function SuppliersPanel({ canMutate }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [editing, setEditing] = useState(null); // {} = new, {...} = existing
  const [error, setError] = useState("");
  const [archiveConfirm, setArchiveConfirm] = useState(null); // supplier

  const refresh = () => {
    setLoading(true);
    api.get(`/admin/suppliers?include_archived=${includeArchived}`)
      .then((r) => setRows(r.data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, [includeArchived]);

  const save = async (form) => {
    setError("");
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone || "",
        email: form.email || "",
        address: form.address || "",
        notes: form.notes || "",
      };
      if (form.id) {
        await api.patch(`/admin/suppliers/${form.id}`, payload);
      } else {
        await api.post("/admin/suppliers", payload);
      }
      setEditing(null);
      refresh();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  const archive = (sup) => setArchiveConfirm(sup);
  const performArchive = async () => {
    const sup = archiveConfirm;
    setArchiveConfirm(null);
    if (!sup) return;
    try {
      await api.delete(`/admin/suppliers/${sup.id}`);
      refresh();
      toast.success(`Fournisseur « ${sup.name} » archivé.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  const unarchive = async (sup) => {
    try {
      await api.patch(`/admin/suppliers/${sup.id}`, { archived: false });
      refresh();
      toast.success(`Fournisseur « ${sup.name} » rétabli.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  return (
    <div data-testid="suppliers-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <h2 className="font-display text-xl font-black text-[#2A0E3C]">Fournisseurs</h2>
          <p className="text-xs text-[#5C4664] mt-0.5">
            Référentiel utilisé par les dépenses « Achat fournisseur » pour les rapports consolidés.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-[#5C4664] flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
              data-testid="suppliers-include-archived"
            />
            Inclure archivés
          </label>
          {canMutate && (
            <Button
              onClick={() => setEditing({})}
              data-testid="supplier-new-btn"
              className="rounded-full bg-[#D81B8C] hover:bg-[#A60F6E] text-white gap-2"
            >
              <Plus className="w-4 h-4" /> Nouveau fournisseur
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-[#5C4664]">Chargement…</p>
      ) : rows.length === 0 ? (
        <div data-testid="suppliers-empty" className="bg-white border border-dashed border-[#F2E2EC] rounded-2xl p-8 text-center text-sm text-[#5C4664]">
          Aucun fournisseur enregistré.
          {canMutate && " Cliquez sur « Nouveau fournisseur » pour démarrer."}
        </div>
      ) : (
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {rows.map((sup) => (
            <li
              key={sup.id}
              data-testid={`supplier-row-${sup.id}`}
              className={`bg-white rounded-2xl border p-4 ${sup.archived ? "border-[#F2E2EC] opacity-60" : "border-[#F2E2EC]"}`}
            >
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-display font-black text-[#2A0E3C] flex-1">{sup.name}</h3>
                {sup.archived && (
                  <span className="text-[10px] font-bold uppercase tracking-widest bg-[#F2E2EC] text-[#5C4664] px-2 py-0.5 rounded-full">
                    Archivé
                  </span>
                )}
              </div>
              <ul className="mt-2 space-y-0.5 text-xs text-[#5C4664]">
                {sup.phone && (
                  <li className="flex items-center gap-1.5"><Phone className="w-3 h-3" />{sup.phone}</li>
                )}
                {sup.email && (
                  <li className="flex items-center gap-1.5"><Mail className="w-3 h-3" />{sup.email}</li>
                )}
                {sup.address && (
                  <li className="flex items-start gap-1.5"><MapPin className="w-3 h-3 mt-0.5" />{sup.address}</li>
                )}
              </ul>
              {canMutate && (
                <div className="mt-3 pt-3 border-t border-[#F2E2EC] flex gap-2">
                  <button
                    onClick={() => setEditing(sup)}
                    data-testid={`supplier-edit-${sup.id}`}
                    className="text-xs font-semibold text-[#2A0E3C] hover:text-[#D81B8C] inline-flex items-center gap-1"
                  >
                    <Pencil className="w-3 h-3" /> Modifier
                  </button>
                  {sup.archived ? (
                    <button
                      onClick={() => unarchive(sup)}
                      data-testid={`supplier-restore-${sup.id}`}
                      className="text-xs font-semibold text-[#3FA535] hover:underline inline-flex items-center gap-1 ml-auto"
                    >
                      <ArchiveRestore className="w-3 h-3" /> Restaurer
                    </button>
                  ) : (
                    <button
                      onClick={() => archive(sup)}
                      data-testid={`supplier-archive-${sup.id}`}
                      className="text-xs font-semibold text-[#A60F6E] hover:underline inline-flex items-center gap-1 ml-auto"
                    >
                      <Archive className="w-3 h-3" /> Archiver
                    </button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {editing !== null && (
        <SupplierFormModal
          initial={editing}
          onCancel={() => { setEditing(null); setError(""); }}
          onSubmit={save}
          error={error}
        />
      )}
      <ConfirmModal
        open={!!archiveConfirm}
        onClose={() => setArchiveConfirm(null)}
        onConfirm={performArchive}
        title="Archiver ce fournisseur ?"
        description={archiveConfirm ? `« ${archiveConfirm.name} » ne sera plus proposé dans les listes déroulantes. L'historique des dépenses sera préservé.` : ""}
        confirmLabel="Archiver"
        testId="supplier-archive-confirm"
      />
    </div>
  );
}

function SupplierFormModal({ initial, onCancel, onSubmit, error }) {
  const [form, setForm] = useState({
    id: initial.id || null,
    name: initial.name || "",
    phone: initial.phone || "",
    email: initial.email || "",
    address: initial.address || "",
    notes: initial.notes || "",
  });
  const submit = (e) => { e.preventDefault(); onSubmit(form); };

  return (
    <div data-testid="supplier-form-modal" className="fixed inset-0 z-50 bg-[#2A0E3C]/70 backdrop-blur-sm flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-3xl w-full max-w-md p-6 space-y-3">
        <div className="flex items-start justify-between">
          <h3 className="font-display text-xl font-black text-[#2A0E3C]">
            {form.id ? "Modifier le fournisseur" : "Nouveau fournisseur"}
          </h3>
          <button type="button" onClick={onCancel} className="text-[#5C4664]" aria-label="Fermer">
            <X className="w-5 h-5" />
          </button>
        </div>
        <Field label="Nom *">
          <Input
            data-testid="supplier-form-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            className="rounded-xl"
          />
        </Field>
        <Field label="Téléphone">
          <Input
            data-testid="supplier-form-phone"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="rounded-xl"
          />
        </Field>
        <Field label="Email">
          <Input
            data-testid="supplier-form-email"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="rounded-xl"
          />
        </Field>
        <Field label="Adresse">
          <Input
            data-testid="supplier-form-address"
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
            className="rounded-xl"
          />
        </Field>
        <Field label="Notes">
          <textarea
            data-testid="supplier-form-notes"
            rows={2}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className="w-full rounded-xl border border-[#F2E2EC] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#D81B8C]"
          />
        </Field>
        {error && <p data-testid="supplier-form-error" className="text-sm text-red-600">{error}</p>}
        <div className="flex gap-2 pt-2">
          <Button type="button" onClick={onCancel} variant="outline" className="flex-1 rounded-full border-[#F2E2EC]">
            Annuler
          </Button>
          <Button type="submit" data-testid="supplier-form-submit" className="flex-1 rounded-full bg-[#D81B8C] hover:bg-[#A60F6E] text-white">
            {form.id ? "Enregistrer" : "Créer"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-bold uppercase tracking-widest text-[#5C4664] mb-1.5 block">
        {label}
      </Label>
      {children}
    </div>
  );
}
